import { useEffect, useMemo, useState } from 'react'
import { createBot, updateBotParams, type BotOut } from '../api'
import { FormulInput } from './FormulaInput'
import { FormulaTester } from './FormulaTester'
import type { Contract } from '../types'

/**
 * `Yeni Bot Kur` / `Botu Düzenle` modalı.
 *
 * Yapı (kullanıcının tarifi, 2026-07-29):
 *   Bot Bilgileri  · Bot ID (otomatik, değiştirilemez) + Bot Tipi + Kontrat
 *                    (tüm AKTİF kontratlar listede, seçili kontrat varsayılan)
 *   Parametreler   · seçilen bot tipine göre değişir (13 ortak parametre)
 *   Alt şerit      · solda `Çalıştır` (Değerlendir aracı); sağda
 *                    `Parametreleri Kaydet` · ↺ · `Kaydet ve Tetikle`
 *
 * Parametre kutuları gerçek girdi (sayı ya da formül); formüller SUNUCUDA,
 * botların kullandığı motorla doğrulanır. Düzenleme modunda (`edit` prop'u)
 * kimlik/tip/kontrat sabittir, yalnız strateji değişir — kaydedince bot bir
 * sonraki turdan itibaren yeni parametrelerle teklif atar.
 */

interface Props {
  /** Varsayılan seçim — panelde o an seçili kontrat. */
  contract: string
  /** Tahtadaki kontratlar; kapalılar elenip listeye aktifler girer. */
  contracts: Contract[]
  onClose: () => void
  /** Bot kurulunca/güncellenince çağrılır — liste anında tazelensin. */
  onCreated?: () => void
  /**
   * Düzenleme modu: verilen botun parametreleri değiştirilir. Kimlik, tip ve
   * kontrat KORUNUR (emir düzenlemesiyle aynı felsefe — bot aynı bot, yalnız
   * stratejisi değişir); üçü de salt görünür.
   */
  edit?: BotOut
}

const BOT_TIPLERI = [
  'ID-TRADING-AL-VE-SAT',
  'ID-TRADING-SAT-VE-AL',
  'BOTTOM-TOP',
  'PARAKETE',
  'BEST-BUYER',
  'BEST-SELLER',
] as const

/**
 * İki ID-TRADING şablonu AYNI bot; tek fark hangi yönde pozisyon AÇAMADIĞI
 * (kullanıcı, 2026-07-29): AL-VE-SAT satarak pozisyon açmaz (`sellMaxAmount`
 * daima 0), SAT-VE-AL alarak açmaz (`buyMaxAmount` daima 0). Açılan pozisyonun
 * KAPATILMASI bu limite tabi değil — bot aldıysa elbette satacak, ama satışı
 * yalnız pozisyon kapatmak için yapacak. Kilitli alan arayüzde sabit 0 ve
 * değiştirilemez.
 *
 * BEST ikilisinde kilit YOK (kullanıcı 2026-08-03): yönü `amount` işareti
 * belirler; BUYER/SELLER ayrımı yalnız kaydedilecek varsayılan setler için.
 */
const KILITLI_PARAMETRE: Partial<Record<string, string>> = {
  'ID-TRADING-AL-VE-SAT': 'sellMaxAmount',
  'ID-TRADING-SAT-VE-AL': 'buyMaxAmount',
}

/**
 * Şablon başına varsayılan parametre seti — kullanıcının verdiği (2026-07-29),
 * formüller OLDUĞU GİBİ kopyalandı. Basic JS; çalışma anında her biri SAYI
 * döndürmeli. İçlerindeki özel değişkenler (RTIME, NETPC, WBUYACTIVEP…) henüz
 * tanımsız — anlamları kullanıcıdan gelecek, değerlendirme motoru sonra.
 *
 * Kullanıcının AL-VE-SAT setindeki `takeProfitOrder: 1` satırı bilerek
 * alınmadı: bizim altkümede o anahtar yok — TP her zaman aktif,
 * `takeProfitLimit=0` kapalı demek (kullanıcı kararı 2026-07-29).
 *
 * BOTTOM-TOP: kilit yok — iki yönü de buy/sellMaxAmount formülleri koşullu
 * açar (ucuzken al, pahalıyken sat). `stopLossTime: -2` bilerek: RTIME hiç
 * negatif olmadığından stop-loss hiç tetiklenmez — bot zararı realize etmez,
 * pozisyonu dengesizliğe bırakabilir (kullanıcı tarifi 2026-07-29).
 */
const VARSAYILANLAR: Partial<Record<string, Record<string, string>>> = {
  'ID-TRADING-AL-VE-SAT': {
    maxPrice: 'Math.max(950, Math.min(1450, WBUYACTIVEP(99,0)+10, WTRADEP(250,0)+50))',
    minPrice: 'Math.min(3700, Math.max(3500, WSELLACTIVEP(99,0)-10, WTRADEP(250,0)-50))',
    sliceBuy: 'Math.abs(NETPC*10)>15? 50: 50',
    sliceSell:
      'Math.abs(NETPC*10)>=200? 100: RTIME<60||Math.abs(NETPC*10)>75? 50: ' +
      'BOTTOTALTRADEBUYQC>25? 25: 0',
    buyMaxAmount: 'WBUYACTIVEP(500,0)>1750 || RTIME<30?0: 250',
    sellMaxAmount: '0',
    rabbitLimit: 'RTIME>60&&Math.abs(NETPC)*10>200? 19 : RTIME>10? 14 : 9',
    deltaPrice: 'RTIME>240? 500: RTIME>60? 750: 1000',
    deltaVolume: 'RTIME>240? 300: RTIME>60? 100: 25',
    takeProfitLimit: 'RTIME>60?395: RTIME>35? 295: RTIME>10? 195: RTIME>2? 95 : 0',
    stopLossTime: '1',
    longStopLossPrice: 'WBUYTRADEBOTP-150',
    shortStopLossPrice: 'WSELLTRADEBOTP+150',
    takeProfitOrder: '1',
    ghostShooter: '1',
    circleBotPosition: '0',
  },
  'ID-TRADING-SAT-VE-AL': {
    maxPrice: 'Math.max(250, Math.min(500, WBUYACTIVEP(99,0)+10, WTRADEP(250,0)+50))',
    minPrice: 'Math.min(2500, Math.max(2050, WSELLACTIVEP(99,0)-10, WTRADEP(250,0)-50))',
    sliceBuy: '20',
    sliceSell: '100',
    buyMaxAmount: '0',
    sellMaxAmount: '100',
    rabbitLimit: 'RTIME>60&&Math.abs(NETPC)*10>200? 19 : RTIME>10? 14 : 9',
    deltaPrice: 'RTIME>240? 500: RTIME>60? 750: 1000',
    deltaVolume: 'RTIME>240? 300: RTIME>60? 100: 25',
    takeProfitLimit: 'RTIME>30? 295: RTIME>6? 195: RTIME>2? 95 : 0',
    stopLossTime: '1',
    longStopLossPrice: 'WBUYTRADEBOTP-150',
    shortStopLossPrice: 'WSELLTRADEBOTP+150',
    takeProfitOrder: '1',
    ghostShooter: '1',
    circleBotPosition: '0',
  },
  'BOTTOM-TOP': {
    maxPrice:
      'Math.max(400,Math.min(Math.max(800,MCP()-1450), WBUYACTIVEP(149,0)+10,' +
      'WTRADEP(500,0)+100))',
    minPrice:
      'Math.min(4550,Math.max(Math.min(4350,MCP()+950), WSELLACTIVEP(149,0)-10,' +
      'WTRADEP(500,0)-50))',
    sliceBuy: '250',
    sliceSell: '250',
    buyMaxAmount:
      'WTRADEP(500,0)<Math.max(2000,MCP()-500) || ' +
      'WBUYACTIVEP(500,0)<Math.max(1750,MCP()-750)? 500: 0',
    sellMaxAmount:
      'WTRADEP(500,0)>Math.min(3500,MCP()+250) || ' +
      'WSELLACTIVEP(500,0)>Math.min(3750,MCP()+500)? 500: 0',
    rabbitLimit: 'RTIME>60&&Math.abs(NETPC)*10>200? 19 : RTIME>10? 14 : 9',
    deltaPrice: 'RTIME>240? 500: RTIME>60? 750: 1000',
    deltaVolume: 'RTIME>240? 300: RTIME>60? 100: 25',
    takeProfitLimit: 'RTIME>10?445: RTIME>4?345: 245',
    stopLossTime: '-2',
    longStopLossPrice: 'WBUYTRADEBOTP+150',
    shortStopLossPrice: 'WSELLTRADEBOTP-150',
    takeProfitOrder: '1',
    ghostShooter: '1',
    circleBotPosition: '0',
  },
  // PARAKETE (kullanıcı seti 2026-08-04, formüller OLDUĞU GİBİ): PTF'ye dönüş
  // botu. Fiyat merdiveni pozisyonla derinleşir (25 lot başına 75 TL), izin
  // circleBotPosition=1 ile pozisyon tavanıdır (100 lot), girişler kapanışa
  // 3 saat / eklemeler 4 saat kala durur, TP maliyet+50 (erken saatte 75),
  // 2 saat kala MCP∓400 hedefli eritme. Veri gerekçesi: 72 kontratlık
  // PTF'ye dönüş geri-testi (2026-08-04).
  'PARAKETE': {
    maxPrice:
      'MCP()==0? 0 : NETPC*10<25? Math.max(MCP()-75,0) : ' +
      'NETPC*10<50? Math.max(MCP()-150,0) : ' +
      'NETPC*10<75? Math.max(MCP()-225,0) : MCP()-300',
    minPrice:
      'MCP()==0? 0 : NETPC*10>-25? Math.min(MCP()+75,4630) : ' +
      'NETPC*10>-50? Math.min(MCP()+150,4630) : ' +
      'NETPC*10>-75? Math.min(MCP()+225,4630) : MCP()+300',
    sliceBuy: 'RTIME<120? 25 : 100',
    sliceSell: 'RTIME<120? 25 : 100',
    buyMaxAmount:
      'MCP()==0 || RTIME<180? 0 : Math.abs(NETPC*10)<25? 100 : RTIME>240? 100 : 0',
    sellMaxAmount:
      'MCP()==0 || RTIME<180? 0 : Math.abs(NETPC*10)<25? 100 : RTIME>240? 100 : 0',
    rabbitLimit: '9',
    deltaPrice: '300',
    deltaVolume: '25',
    takeProfitLimit: 'RTIME>300? 75: 50',
    stopLossTime: '120',
    longStopLossPrice: 'Math.max(0, MCP()-400)',
    shortStopLossPrice: 'Math.min(4630, MCP()+400)',
    takeProfitOrder: '1',
    ghostShooter: '1',
    circleBotPosition: '1',
  },
  // Kullanıcının verdiği set (2026-08-03), formüller OLDUĞU GİBİ. Sabitler
  // kontrata göre elle değiştirilecek. minPriceLimit alış botunda işlevsiz
  // ama bilinçli dolu: MAXDAMP()*1.0295 = dengesizlik tavanı — yanlışlıkla
  // satış yönü açılırsa taban gökyüzünde dursun.
  'BEST-BUYER': {
    amount:
      'RTIME<150&&WTRADEP(500,0)>MCP()+150? 5 : ' +
      'RTIME>49&&NETPC<DAMQC()*0.7-2? -DAMQC()*0.3*10: RTIME>59? 4 : 5',
    maxPriceLimit:
      'Math.min(4634,Math.max(MCP()-945, 10,' +
      '(WSELLACTIVEP(980,0)-WSELLACTIVEP(98,0)>98&&' +
      'WSELLACTIVEP(99,0)<MCP()-(RTIME<5?-95:RTIME<20||NETPC<(DAMQC()*0.7)?475:845)?' +
      'Math.min(WSELLACTIVEP(99,0)+9,WTRADEP(250,0)+49):' +
      'RTIME<100&&SELLACTIVEQ(MCP()+150,0)<100&&' +
      '(WTRADEP(500,0)>MCP()+149||BUYACTIVEQ(MCP()+149,0)>200)?' +
      'Math.max(Math.min(MCP()+(RTIME>75?205:RTIME>40?305:RTIME>2?405:505),' +
      'WTRADEP(600,0)+50,(RTIME<59?WBUYACTIVEP(99,0)+9:WSELLACTIVEP(99,0)+9)), ' +
      'WBUYACTIVEP(RTIME>55?899:RTIME>20?499:249,0)+9):' +
      'RTIME>3&&(SELLACTIVEQ(MCP()-99,0)>99||SELLACTIVEQ(MCP()+25,0)>200||' +
      'BUYACTIVEQ(MCP()-200,0)<200)?' +
      'Math.min(MCP()-(RTIME<7?75:RTIME<18||NETPC<(DAMQC()*0.7)?245:375), ' +
      'WBUYACTIVEP(99,0)+9, WTRADEP(250,0)+50):' +
      'Math.max(Math.min(MCP()+(RTIME>110?-75:RTIME>45?95:RTIME>3?155:245),' +
      'WTRADEP(500,0)+99,(RTIME>12?WBUYACTIVEP(RTIME>50?149:99,0):' +
      'WSELLACTIVEP(99, 0))+9), WBUYACTIVEP(RTIME>90?1250:RTIME>40?599:299,0)+15))))',
    minPriceLimit: 'MAXDAMP()*1.0295',
    slice: 'Math.abs(NETPC)>=75? 75: Math.abs(NETPC)>=55? 50: 25',
    rabbitLimit: 'RTIME>60&&Math.abs(NETPC)*10>200? 24: RTIME<=20? 9: 15',
    deltaPrice: 'RTIME>240? 500: RTIME>60? 750: 1000',
    deltaVolume: 'RTIME>240? 300: RTIME>60? 100: 25',
    shooter: '1',
    maxAmountForThisBot: '1',
  },
  // Kullanıcının verdiği set (2026-08-03), formüller OLDUĞU GİBİ (yalnız
  // satır kırılımları tek satıra indi — girdi kutusu tek satırlık, parser
  // için fark yok). Sabit sayılar kontrata göre elle değiştirilecek.
  // maxPriceLimit kullanıcı vermedi: satış botunda işlevsiz, '0' bırakıldı.
  'BEST-SELLER': {
    amount:
      'RTIME<150&&WTRADEP(500,0)<MCP()-150? -5 : ' +
      'RTIME>39&&NETPC>DAMQC()*0.7-2? -DAMQC()*0.3*10: RTIME>59? -4 : -5',
    maxPriceLimit: '0',
    minPriceLimit:
      'Math.max(1,Math.min(MCP()+945, 4630,' +
      '(WBUYACTIVEP(98,0)-WBUYACTIVEP(980,0)>99&&' +
      'WBUYACTIVEP(99,0)>MCP()+(RTIME<5?-95:RTIME<20||NETPC>(DAMQC()*0.7)?475:845)?' +
      'Math.max(WBUYACTIVEP(99,0)-9,WTRADEP(250,0)-49):' +
      'RTIME<100&&BUYACTIVEQ(MCP()-150,0)<100&&' +
      '(WTRADEP(600,0)<MCP()-149||SELLACTIVEQ(MCP()-149,0)>200)?' +
      'Math.min(Math.max(MCP()-(RTIME>75?205:RTIME>40?305:RTIME>2?405:505),' +
      'WTRADEP(600,0)-50,(RTIME>59?WSELLACTIVEP(99,0)-9:WBUYACTIVEP(99,0)-9)),' +
      'WSELLACTIVEP(RTIME>55?899:RTIME>20?499:249,0)-9):' +
      'RTIME>3&&(BUYACTIVEQ(MCP()+99,0)>99||BUYACTIVEQ(MCP()-25,0)>200||' +
      'SELLACTIVEQ(MCP()+200,0)<200)?' +
      'Math.max(MCP()+(RTIME<7?75:RTIME<18||NETPC>(DAMQC()*0.7)?245:375), ' +
      'WSELLACTIVEP(99,0)-9, WTRADEP(250,0)-50):' +
      'Math.min(Math.max(MCP()-(RTIME>110?-75:RTIME>45?95:RTIME>3?155:245),' +
      'WTRADEP(500,0)-99,(RTIME>12?WSELLACTIVEP(RTIME>50?149:99,0):' +
      'WBUYACTIVEP(99,0))-9),' +
      'WSELLACTIVEP(RTIME>90?1250:RTIME>40?599:299,0)-15))))',
    slice: 'Math.abs(NETPC)>=75? 75: Math.abs(NETPC)>=55? 50: 25',
    rabbitLimit: 'RTIME>60&&Math.abs(NETPC)*10>200? 24: RTIME<=20? 9: 15',
    deltaPrice: 'RTIME>240? 500: RTIME>60? 750: 1000',
    deltaVolume: 'RTIME>240? 300: RTIME>60? 100: 25',
    shooter: '1',
    maxAmountForThisBot: '1',
  },
}

/** Kullanıcının kaydettiği şablon varsayılanları — tarayıcının kalıcı deposunda.

    `Parametreleri Kaydet` o anki seti şablonun varsayılanı yapar; ↺ fabrika
    setine (yukarıdaki VARSAYILANLAR) döndürür. Sunucuya taşınmadı (bilerek):
    bu bir kullanıcı tercihi, bot kaydı değil — botun kendi parametreleri
    kurulumda zaten Postgres'e yazılıyor. */
const SAKLI_ANAHTAR = 'shadow.botDefaults.v1'

function kayitliVarsayilanlar(): Record<string, Record<string, string>> {
  try {
    return JSON.parse(localStorage.getItem(SAKLI_ANAHTAR) ?? '{}')
  } catch {
    return {}                 // bozuk kayıt varsayılanları engellemesin
  }
}

function varsayilanKaydet(tip: string, degerler: Record<string, string> | null) {
  const hepsi = kayitliVarsayilanlar()
  if (degerler === null) delete hepsi[tip]
  else hepsi[tip] = degerler
  localStorage.setItem(SAKLI_ANAHTAR, JSON.stringify(hepsi))
}

/** Şablonun başlangıç değerleri: kullanıcı kaydı > fabrika seti > boş. */
function varsayilanSet(tip: string, fabrika = false): Record<string, string> {
  const bos = Object.fromEntries(paramTanimlari(tip).map((p) => [p.name, '']))
  return {
    ...bos,
    ...(VARSAYILANLAR[tip] ?? {}),
    ...(fabrika ? {} : (kayitliVarsayilanlar()[tip] ?? {})),
  }
}

interface ParamTanim {
  name: string
  birim: string
  aciklama: string
  /** Onay kutusu olarak çizilir (kullanıcı isteği 2026-08-03) — değeri yine
      "0"/"1" formülü olarak sunucuya gider, şemada özel durum yok. */
  secim?: boolean
}

/**
 * ID-TRADING parametreleri — sıra ve açıklamalar kullanıcının
 * verdiği (2026-07-29). Her kutu SAYI döndürmek zorunda: sabit de yazılsa
 * formül de yazılsa sonuç bir sayıya inmeli.
 */
const ID_TRADING_PARAMETRELERI: ParamTanim[] = [
  {
    name: 'maxPrice',
    birim: 'TRY',
    aciklama: 'Alış yapılan durumlar için hedef fiyatı belirler.',
  },
  {
    name: 'minPrice',
    birim: 'TRY',
    aciklama: 'Satış yapılan durumlar için hedef fiyatı belirler.',
  },
  {
    name: 'sliceBuy',
    birim: 'Lot',
    aciklama:
      'Tahtaya verilecek her bir alış teklifinin kaç lot olması gerektiğini ifade eder.',  },
  {
    name: 'sliceSell',
    birim: 'Lot',
    aciklama:
      'Tahtaya verilecek her bir satış teklifinin kaç lot olması gerektiğini ifade eder.',  },
  {
    name: 'buyMaxAmount',
    birim: 'Lot',
    aciklama:
      'İlgili kontratta yapılabilecek maksimum alış miktarını temsil eder. ' +
      'Pozisyon açma limitidir — açılan pozisyonun kapatılması bu limite tabi değildir.',  },
  {
    name: 'sellMaxAmount',
    birim: 'Lot',
    aciklama:
      'İlgili kontratta yapılabilecek maksimum satış miktarını temsil eder. ' +
      'Pozisyon açma limitidir — açılan pozisyonun kapatılması bu limite tabi değildir.',  },
  {
    name: 'rabbitLimit',
    birim: 'Lot',
    aciklama:
      'Botun hedef fiyat dahilinde en iyi teklif olması için önündeki tekliflerin ' +
      'toplamda hangi miktara ulaşması gerektiğini ifade eder.',  },
  {
    name: 'deltaPrice',
    birim: 'TRY',
    aciklama:
      'Tahtadaki en iyi teklif ile sizin teklifiniz arasındaki fiyat farkını temsil ' +
      'eder. Eğer aradaki fark buraya girilen değerden büyük ise bot en iyi teklif ' +
      'olmaya çalışmaz.',  },
  {
    name: 'deltaVolume',
    birim: 'Lot',
    aciklama:
      "Buraya girilen hacime eşit ya da daha büyük bir hacim deltaPrice'ın altında " +
      'varsa deltaPrice parametresi dikkate alınmaz.',  },
  {
    name: 'takeProfitLimit',
    birim: 'TRY',
    aciklama:
      'Açılan pozisyon dahilinde ne kadar birim kar elde edilmek istendiğinin ' +
      'girildiği parametredir. 0 girilirse kâr alma mantığı devre dışı kalır, ' +
      'bot doğrudan maxPrice/minPrice hedefleriyle çalışır.',  },
  {
    name: 'stopLossTime',
    birim: 'Dk.',
    aciklama:
      'Eğer botun açtığı pozisyon kapatılmak isteniyorsa, kapı kapanışına kaç dakika ' +
      'kala pozisyonun kapatılması gerektiği girildiği parametredir.',  },
  {
    name: 'longStopLossPrice',
    birim: 'TRY',
    aciklama:
      'Kapı kapanışına stopLossTime parametresine girilen dakika kadar kaldıysa ve ' +
      'pozisyon pozitif ise bot bu parametreye girilen değer dahilinde teklif atar.',  },
  {
    name: 'shortStopLossPrice',
    birim: 'TRY',
    aciklama:
      'Kapı kapanışına stopLossTime parametresine girilen dakika kadar kaldıysa ve ' +
      'pozisyon negatif ise bot bu parametreye girilen değer dahilinde teklif atar.',  },
  {
    name: 'takeProfitOrder',
    birim: '0 || 1',
    aciklama:
      '"0" girilirse kâr alma özelliği devre dışı kalır — bot yalnız maxPrice ' +
      '(alışta) ve minPrice (satışta) hedefleriyle yarışır. "1" girilirse bot ' +
      'açık pozisyonun maliyeti üzerine takeProfitLimit kadar kârı ekleyerek ' +
      'yeni bir hedef oluşturur: satış yönlü MIN(minPrice, Maliyet+TP), alış ' +
      'yönlü MAX(maxPrice, Maliyet−TP).',
  },
  {
    name: 'ghostShooter',
    birim: '0 || 1',
    aciklama:
      '1 girilirse, ghostshooter özelliği devreye girerek karşı tarafa gelen ' +
      'uygun teklifler vurulur. 0 girilirse devre dışı kalır.',
  },
  {
    name: 'circleBotPosition',
    birim: '0 || 1',
    aciklama:
      '"0" girilirse buyMaxAmount/sellMaxAmount ömürlük açma bütçesidir: ' +
      'kapanan pozisyon hakkı geri vermez, bütçe bitince o taraf bir daha ' +
      'açamaz. "1" girilirse limit mevcut pozisyonun tavanı olur: pozisyon ' +
      'kapandıkça açma hakkı kendiliğinden yenilenir (tur tur açıp kapatan ' +
      'stratejiler için).',
  },
]

/**
 * BEST-BUYER / BEST-SELLER parametreleri — kullanıcının tarifi (2026-08-03).
 * Tek yönlü emir yürütme botu: `amount` kadar lotu hedef fiyat sınırını
 * aşmadan doldurur, dolunca kapalı konuma geçer. Yönü `amount` işareti
 * belirler; kullanılmayan yönün fiyat limiti şablonda durur ama işlevsizdir.
 */
const BEST_PARAMETRELERI: ParamTanim[] = [
  {
    name: 'amount',
    birim: 'Lot',
    aciklama:
      'İlgili kontratta kapatılacak pozisyonun miktarını ve yönünü belirler. ' +
      'Negatif değer girilirse bot satış işlemi yapar. Pozitif değer girilirse ' +
      'bot alış işlemi yapar.',
  },
  {
    name: 'maxPriceLimit',
    birim: 'TRY',
    aciklama: 'Alış hedef fiyatını temsil eder. (Bot satış yönündeyken işlevsizdir.)',
  },
  {
    name: 'minPriceLimit',
    birim: 'TRY',
    aciklama: 'Satış hedef fiyatını temsil eder. (Bot alış yönündeyken işlevsizdir.)',
  },
  {
    name: 'slice',
    birim: 'Lot',
    aciklama:
      'Tahtaya verilecek her bir teklifin kaç lot olması gerektiğini ifade eder.',
  },
  {
    name: 'rabbitLimit',
    birim: 'Lot',
    aciklama:
      'Botun hedef fiyat dahilinde en iyi teklif olması için önündeki tekliflerin ' +
      'toplamda hangi miktara ulaşması gerektiğini ifade eder.',
  },
  {
    name: 'deltaPrice',
    birim: 'TRY',
    aciklama:
      'Tahtadaki en iyi teklif ile sizin teklifiniz arasındaki fiyat farkını temsil ' +
      'eder. Eğer aradaki fark buraya girilen değerden büyük ise bot en iyi teklif ' +
      'olmaya çalışmaz.',
  },
  {
    name: 'deltaVolume',
    birim: 'Lot',
    aciklama:
      "Buraya girilen hacime eşit ya da daha büyük bir hacim deltaPrice'ın altında " +
      'varsa deltaPrice parametresi dikkate alınmaz.',
  },
  {
    name: 'shooter',
    birim: 'Mantıksal',
    secim: true,
    aciklama:
      'Seçili ise ghost shooter özelliği devreye girerek karşı tarafa gelen ' +
      'uygun teklifler vurulur.',
  },
  {
    name: 'maxAmountForThisBot',
    birim: 'Mantıksal',
    secim: true,
    aciklama:
      'Seçili ise sadece botun yaptığı işlemler dikkate alınır. Seçili değilse ' +
      'kontrattaki tüm eşleşmeler sayılır (kaynağı fark etmez — yalnız bot ' +
      'kurulduktan sonrakiler).',
  },
]

/** Tip → parametre tanımları. İlk üç şablon aynı 15'lik ID-TRADING şeması. */
const SABLON_PARAMETRELERI: Record<string, ParamTanim[]> = {
  'ID-TRADING-AL-VE-SAT': ID_TRADING_PARAMETRELERI,
  'ID-TRADING-SAT-VE-AL': ID_TRADING_PARAMETRELERI,
  'BOTTOM-TOP': ID_TRADING_PARAMETRELERI,
  'BEST-BUYER': BEST_PARAMETRELERI,
  'BEST-SELLER': BEST_PARAMETRELERI,
}

function paramTanimlari(tip: string): ParamTanim[] {
  return SABLON_PARAMETRELERI[tip] ?? ID_TRADING_PARAMETRELERI
}

/** `BT` + yıl ay gün saat dakika saniye — emir kimliğiyle (`OR…`) aynı dil. */
function botIdUret(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return (
    `BT${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
    `${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
  )
}

export function NewBotModal({ contract, contracts, onClose, onCreated, edit }: Props) {
  // Kimlik modal açıldığı anda bir kez üretilir; düzenlemede MEVCUT kimlik.
  const uretilen = useMemo(botIdUret, [])
  const botId = edit ? edit.id : uretilen
  const [tip, setTip] = useState<string>(edit ? edit.type : BOT_TIPLERI[0])
  const [kontrat, setKontrat] = useState(edit ? edit.contract : contract)
  const [degerler, setDegerler] = useState<Record<string, string>>(() =>
    edit
      ? { ...varsayilanSet(edit.type, true), ...edit.params }
      : varsayilanSet(BOT_TIPLERI[0]),
  )
  //: `Çalıştır` → Değerlendir test aracı (modalın sağ üstünde ayrı kart).
  const [testerAcik, setTesterAcik] = useState(false)
  const [kaydediliyor, setKaydediliyor] = useState(false)
  const [kayitHatasi, setKayitHatasi] = useState<string | null>(null)
  //: `Parametreleri Kaydet` geri bildirimi — kısa süreli "Kaydedildi ✓".
  const [setKaydedildi, setSetKaydedildi] = useState(false)

  // Kapalı kontrata bot kurulmaz — onlar listeye hiç girmiyor. Seçili kontrat
  // kapalıysa (arşivden inceleniyor olabilir) liste yine aktiflerle dolu,
  // varsayılan ilk aktif kontrata düşer. Düzenlemede kontrat SABİT: botun
  // kendi kontratı (aktif listede olmasa bile — bot orada yaşıyor).
  const aktifler = useMemo(() => contracts.filter((c) => !c.closed).map((c) => c.key), [contracts])
  const secili = edit
    ? edit.contract
    : aktifler.includes(kontrat)
      ? kontrat
      : (aktifler[0] ?? '')

  // Esc ile kapanma — emir modalıyla aynı davranış.
  useEffect(() => {
    const kapat = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', kapat)
    return () => window.removeEventListener('keydown', kapat)
  }, [onClose])

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      {/* Gövdeye tıklamak kapatmamalı; yalnız arka plan kapatır. */}
      <div
        className="bot-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Yeni bot kur"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <span className="modal-title">
            {edit ? 'Botu Düzenle' : 'Yeni Bot Kur'}
            {edit && <span className="muted"> · kimlik ve geçmiş korunur</span>}
          </span>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Kapat">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-section">Bot Bilgileri</div>
          <div className="bot-info-grid">
            <label className="bot-field">
              <span className="bot-field-label">
                Bot ID <em>otomatik · değiştirilemez</em>
              </span>
              <input className="bot-id" value={botId} readOnly tabIndex={-1} />
            </label>
            <label className="bot-field">
              <span className="bot-field-label">Bot Tipi</span>
              <select
                className="bot-select"
                value={tip}
                disabled={!!edit}      // düzenlemede tip sabit — kilit anlamı değişirdi
                onChange={(e) => {
                  setTip(e.target.value)
                  // Şablon değişince kutular O şablonun varsayılanlarına döner:
                  // parametre seti şablona ait, önceki girdiyi taşımak yanıltır.
                  setDegerler(varsayilanSet(e.target.value))
                }}
              >
                {BOT_TIPLERI.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="bot-field">
              <span className="bot-field-label">Kontrat</span>
              <select
                className="bot-select"
                value={secili}
                disabled={!!edit}      // düzenlemede kontrat sabit — başka piyasa başka bot
                onChange={(e) => setKontrat(e.target.value)}
              >
                {(edit ? [edit.contract] : aktifler).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="modal-section">Parametreler</div>
          <div className="bot-params">
            {paramTanimlari(tip).map((p) => {
              const sabit = p.name === KILITLI_PARAMETRE[tip]
              const kilitNotu = sabit
                ? `Bu şablonda ${tip === 'ID-TRADING-AL-VE-SAT' ? 'satış' : 'alış'} ` +
                  'yönünde pozisyon açılmaz — bu parametre her zaman 0.'
                : undefined
              return (
                <div className="bot-param" key={p.name}>
                  <div className="bot-param-head">
                    <span className="bot-param-code" aria-hidden="true">
                      {'< >'}
                    </span>
                    <span className="bot-param-name">{p.name}</span>
                    <span className="bot-param-unit">{p.birim}</span>
                    {/* ⓘ — fareyle ya da klavye odağıyla açılan açıklama balonu. */}
                    <span
                      className="bot-param-info"
                      tabIndex={0}
                      aria-label={`${p.name}: ${p.aciklama}`}
                    >
                      i
                      <span className="bot-tooltip" role="tooltip">
                        <b>
                          {p.name} <i>· {p.birim}</i>
                        </b>
                        <span>{p.aciklama}</span>
                        {kilitNotu && <span className="bot-tooltip-lock">{kilitNotu}</span>}
                        <em>
                          {p.secim
                            ? 'Onay kutusu — açık 1, kapalı 0 olarak kaydedilir.'
                            : 'Sabit ya da formül — sonuç bir sayı olmalı.'}
                        </em>
                      </span>
                    </span>
                  </div>
                  {p.secim ? (
                    /* Mantıksal parametre (kullanıcı isteği 2026-08-03): gerçek
                       onay kutusu; değer sunucuya "0"/"1" olarak gider. */
                    <label className="bot-param-toggle">
                      <input
                        type="checkbox"
                        checked={degerler[p.name] === '1'}
                        onChange={(e) =>
                          setDegerler((d) => ({ ...d, [p.name]: e.target.checked ? '1' : '0' }))
                        }
                      />
                      <span>{degerler[p.name] === '1' ? 'Açık' : 'Kapalı'}</span>
                    </label>
                  ) : (
                    <FormulInput
                      value={degerler[p.name] ?? ''}
                      onChange={(v) => setDegerler((d) => ({ ...d, [p.name]: v }))}
                      locked={sabit}
                      lockNote={kilitNotu}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {kayitHatasi && (
          <div className="bot-save-error" role="alert">
            {kayitHatasi}
          </div>
        )}
        <div className="bot-modal-foot">
          {/* Değerlendir aracını açar/kapar — formülü kaydetmeden dene. */}
          <button
            type="button"
            className="bot-btn secondary"
            aria-pressed={testerAcik}
            onClick={() => setTesterAcik((v) => !v)}
          >
            <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
              <path d="M2.5 1.5 10 6 2.5 10.5Z" fill="currentColor" />
            </svg>
            Çalıştır
          </button>
          <span className="bot-foot-right">
            {/* O anki seti bu şablonun varsayılanı yap (tarayıcı deposu). */}
            <button
              type="button"
              className="bot-btn secondary"
              onClick={() => {
                varsayilanKaydet(tip, { ...degerler })
                setSetKaydedildi(true)
                setTimeout(() => setSetKaydedildi(false), 1500)
              }}
            >
              {setKaydedildi ? 'Kaydedildi ✓' : 'Parametreleri Kaydet'}
            </button>
            {/* Fabrika setine dön: kayıtlı varsayılanı sil + kutuları doldur. */}
            <button
              type="button"
              className="bot-btn secondary bot-reset"
              title="Varsayılana döndür — şablonun ilk yüklenen parametre seti"
              aria-label="Varsayılana döndür"
              onClick={() => {
                varsayilanKaydet(tip, null)
                setDegerler(varsayilanSet(tip, true))
              }}
            >
              <svg viewBox="0 0 14 14" width="13" height="13" aria-hidden="true">
                <path
                  d="M11.8 7a4.8 4.8 0 1 1-1.4-3.4M11.8 1.6v2.6H9.2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          <button
            type="button"
            className="bot-btn primary"
            disabled={kaydediliyor || !secili}
            onClick={async () => {
              // Formüller SUNUCUDA doğrulanır (motorla, kayıt anında) —
              // bozuk formül parametre adıyla 422 döner ve burada gösterilir.
              setKaydediliyor(true)
              setKayitHatasi(null)
              try {
                if (edit) {
                  // Kimlik korunur; bot bir sonraki turdan itibaren yeni
                  // stratejiyle teklif atar.
                  await updateBotParams(edit.id, degerler)
                } else {
                  await createBot({ type: tip, contract: secili, params: degerler })
                }
                onCreated?.()
                onClose()
              } catch (err) {
                setKayitHatasi(
                  err instanceof Error ? err.message : edit ? 'Bot güncellenemedi' : 'Bot kurulamadı',
                )
              } finally {
                setKaydediliyor(false)
              }
            }}
          >
            {kaydediliyor
              ? edit ? 'Kaydediliyor…' : 'Kuruluyor…'
              : edit ? 'Kaydet' : 'Kaydet ve Tetikle'}
          </button>
          </span>
        </div>
      </div>

      {testerAcik && (
        <FormulaTester
          contract={secili}
          contracts={aktifler}
          onClose={() => setTesterAcik(false)}
        />
      )}
    </div>
  )
}
